import React, { type ReactNode } from "react";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import { cva, type VariantProps } from "class-variance-authority";

import { cn } from "../../lib/utils";

const modalVariants = cva(
  "data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 fixed inset-x-0 top-[92px] z-50 mx-auto flex max-h-[calc(100dvh-104px)] w-[calc(100%-2.5rem)] max-w-lg flex-col overflow-hidden rounded-xl border border-border bg-background-primary shadow-lg duration-200 data-[state=closed]:animate-out data-[state=open]:animate-in",
  {
    variants: {
      size: {
        default: "max-w-lg",
        sm: "max-w-sm",
        lg: "max-w-2xl",
        xl: "max-w-4xl",
      },
    },
    defaultVariants: {
      size: "default",
    },
  },
);

export const DropdownModalHeader = ({ children }: { children: ReactNode }) => {
  return (
    <DialogPrimitive.Title asChild>
      <div className="type-sm-emphasis flex items-center justify-between border-b px-4 py-3">{children}</div>
    </DialogPrimitive.Title>
  );
};

export const DropdownModalFooter = ({ children }: { children: ReactNode }) => {
  return <div className="flex h-[52px] items-center justify-between border-t px-2 py-1.5">{children}</div>;
};

export const DropdownModalBody = ({
  children,
  className,
  noPadding,
}: {
  children: ReactNode;
  className?: string;
  noPadding?: boolean;
}) => {
  return (
    <div className={cn("min-h-0 flex-1 space-y-2 overflow-y-auto overflow-x-hidden", noPadding && "px-0", className)}>
      {children}
    </div>
  );
};

export const DropdownModalTrigger = DialogPrimitive.Trigger;

export const DropdownModal = ({ ...props }: React.ComponentProps<typeof DialogPrimitive.Root>) => (
  <DialogPrimitive.Root {...props} />
);

export const DropdownModalContent = ({
  ref,
  children,
  size,
  className,
  ariaLabel,
  ...props
}: React.ComponentPropsWithoutRef<typeof DialogPrimitive.Content> &
  VariantProps<typeof modalVariants> & { ref?: React.Ref<HTMLDivElement>; ariaLabel?: string }) => (
  <DialogPrimitive.Portal>
    <DialogPrimitive.Overlay className="data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 fixed inset-0 z-50 bg-black/80 data-[state=closed]:animate-out data-[state=open]:animate-in" />

    <DialogPrimitive.Content ref={ref} className={cn(modalVariants({ size }), className)} {...props}>
      {ariaLabel !== undefined && (
        <DialogPrimitive.Title asChild>
          <span className="sr-only">{ariaLabel}</span>
        </DialogPrimitive.Title>
      )}
      <div className="flex min-h-0 min-w-0 flex-1 flex-col">{children}</div>
    </DialogPrimitive.Content>
  </DialogPrimitive.Portal>
);

DropdownModal.displayName = "DropdownModal";
